import { useUserListBox } from "@jugl-web/domain-resources/users/hooks/useUserListBox";
import { useMultipleUserProfiles } from "@jugl-web/domain-resources/users/hooks/useMultipleUserProfiles";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { InteractiveContainer } from "@jugl-web/ui-components/cross-platform";
import { ResourcePickerPopover } from "@jugl-web/ui-components/web/ResourcePickerPopover";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { useMemo } from "react";
import { Tooltip } from "@jugl-web/ui-components/web";
import { useCPanelPageContext } from "../../../../useCPanelPageContext";
import { ReactComponent as UserIcon } from "./assets/user.svg";
import { ReactComponent as InfoIcon } from "./assets/info-icon.svg";

export const UserManagers: React.FC<{
  managers: string[];
  userId: string;
  userEntityRelId: string;
}> = ({ managers, userId, userEntityRelId }) => {
  const { t } = useTranslations();
  const { entityId } = useEntitySelectedProvider();
  const { toast } = useToast({ variant: "web" });
  const { getListBoxProps, clearSearchQuery } = useUserListBox({
    entityId,
    showDepartmentFilters: true,
    topUsers: managers,
    excludeUsers: [userId],
  });

  const { profiles } = useMultipleUserProfiles({
    entityId,
    userIds: managers,
  });
  const { usersApi } = useRestApiProvider();
  const [updateUser] = usersApi.useUpdateUserMutation();
  const { updateUser$ } = useCPanelPageContext();

  const handleManagersChange = async (selectedManagers: string[]) => {
    const response = await updateUser({
      entityId,
      entityRelId: userEntityRelId,
      data: { managers: selectedManagers },
    });
    if (response && "data" in response) {
      updateUser$.next({
        id: userEntityRelId,
        data: { managers: selectedManagers },
      });
      toast(
        t({
          id: "cpanel-page.managers-successfully-updated",
          defaultMessage: "Managers successfully updated",
        })
      );
    }
  };

  const managersProfiles = useMemo(
    () =>
      profiles.map((el) => ({
        firstName: el.firstName || "",
        lastName: el.lastName || "",
        imageUrl: el.avatar || "",
      })),
    [profiles]
  );

  return (
    <ResourcePickerPopover
      placement="bottom-end"
      title={
        <div className="flex items-center gap-3">
          <span>
            {t({
              id: "cpanel-page.direct-managers",
              defaultMessage: "Direct managers",
            })}
          </span>
          <Tooltip
            placement="bottom-end"
            className="z-tooltip p-4"
            delay="none"
            renderTrigger={({ ref, props }) => (
              <InfoIcon ref={ref} className="cursor-help" {...props} />
            )}
          >
            {t({
              id: "cpanel-page.direct-managers-description",
              defaultMessage:
                "Direct Managers can see Tasks, Reports and other work flow information 📊",
            })}
          </Tooltip>
        </div>
      }
      className="w-[315px]"
      renderTrigger={({ Trigger, triggerRef }) => (
        <Trigger
          ref={triggerRef}
          as={InteractiveContainer}
          className="w-full px-1 py-2 hover:bg-grey-100"
        >
          <div className="flex items-center gap-1">
            <UserIcon />
            {managersProfiles.length ? (
              <span className="text-sm font-medium text-[#4F4F4F]">
                {t(
                  {
                    id: "cpanel-page.managers-count",
                    defaultMessage:
                      "{count} {count, plural, one {Manager} other {Managers}}",
                  },
                  { count: managersProfiles.length }
                )}
              </span>
            ) : (
              <span className="text-sm text-grey-700">
                {t({
                  id: "cpanel-page.tap-to-set",
                  defaultMessage: "Tap to set",
                })}
              </span>
            )}
          </div>
        </Trigger>
      )}
      onUnmount={clearSearchQuery}
      onSubmit={handleManagersChange}
      {...getListBoxProps({
        selectionBehavior: { mode: "multiple" },
        defaultSelectedIds: managers,
        maxVisibleItems: 5,
      })}
    />
  );
};
